import dayjs from 'dayjs';
import type { ModelCancellationPolicy, ModelOrderResponse } from '@layup/api-sdk';
import { updateOrder } from '../services/orders';
import { displayCurrency, formatDateMedium } from './format.utils';

const createCancellationPolicy = (
  type: string,
  percentage: number = 0,
  flatFee: number = 0,
): ModelCancellationPolicy => {
  switch (type) {
    case 'Percentage':
      return {
        name: 'Percentage',
        definition: {
          percentage: parseFloat(percentage.toString()),
          flatFee: 0,
          deposit: false,
        },
      };
    case 'Flat Fee':
      return {
        name: 'FlatFee',
        definition: {
          percentage: 0,
          flatFee,
          deposit: false,
        },
      };
    case 'Deposit':
      return {
        name: 'Deposit',
        definition: {
          percentage: 0,
          flatFee: 0,
          deposit: true,
        },
      };
    case 'No Fee':
      return {
        name: 'FlatFee',
        definition: {
          percentage: 0,
          flatFee: 0,
          deposit: false,
        },
      };
    default:
      throw new Error(`Unknown cancellation type: ${type}`);
  }
};

/**
 * Sends a notification for an order.
 * @param order The order to send the notification for.
 * @param cancellationType The type of cancellation.
 * @param message The message to include in the notification.
 * @param user The user details on the cancellation.
 * @param percentage The percentage for the cancellation fee, if applicable. 10 would be 10%.
 * @param fee The flat fee for the cancellation, if applicable in cents.
 */
export const sendOrderNotification = async (
  order: ModelOrderResponse,
  cancellationType: string,
  message: string,
  user: { id: string; name?: string },
  percentage?: number,
  fee?: number,
) => {
  // This is a response to an existing note so there should always be notifications
  if (!order.notifications) return;

  const cancellationTerms = {
    definition: [
      {
        policy: createCancellationPolicy(cancellationType, percentage, fee),
        type: order.cancellationTerms?.definition?.[0].type,
        start: 0,
        end: 0,
      },
    ],
  };

  const notifications = [...order.notifications];
  const lastIndex = notifications.length - 1;

  // Mark previous note as read
  notifications[lastIndex] = {
    ...notifications[lastIndex],
    read: true,
    type: 'CANCELLATION',
  };

  notifications.push({
    change: notifications[lastIndex].change,
    date: new Date().toISOString(),
    name: user.name,
    reason: notifications[lastIndex].reason,
    details: `Cancellation Fee: ${displayCurrency(fee)} - Message: ${message}`,
    userId: user.id,
    type: 'CANCELLATION',
  });

  const orderUpdateRequest = {
    orderCancellationRequest: order.orderCancellationRequest,
    orderRefundRequest: undefined,
    notifications,
    state: order.state,
    cancellationTerms,
    groupPaymentPlan: order.groupPaymentPlan,
    orderRefundFee: order.orderRefundFee,
    orderRefundAmount: order.orderRefundAmount,
  };

  await updateOrder(order._id!, orderUpdateRequest);

  // Only mutate the original order after a successful API call
  order.cancellationTerms = cancellationTerms;
  order.notifications = notifications;
};
